import { ThemeSwitch, useThemedPage } from '@/components/marketing/marketing-theme';
import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { onAuthStateChanged, type User } from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { MobileMenuDrawer } from './mobile-menu-drawer';
import { MegaMenu } from './mega-menu';

export function MarketingNav() {
  const [user, setUser] = useState<User | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, setUser);
    return unsub;
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const themedPage = useThemedPage();
  const closeMenu = useCallback(() => setMobileOpen(false), []);
  const solid = scrolled || mobileOpen;

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 transition-all duration-200 ${mobileOpen ? 'z-[60]' : 'z-40'}`}
        style={{
          background: mobileOpen ? 'var(--warm-page)' : solid ? 'var(--warm-page)' : 'transparent',
          borderBottom: solid ? '1px solid var(--warm-border)' : '1px solid transparent',
        }}
      >
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <img width={24} height={24} src="/assets/logo_square.svg" alt="" className="w-6 h-6 dark:invert" />
            <span className="font-display font-light text-lg" style={{ color: 'var(--warm-text)' }}>Kora</span>
          </Link>

          <MegaMenu />

          <div className="flex items-center gap-2">
            {themedPage && <span className="hidden lg:inline-flex"><ThemeSwitch /></span>}
            {user ? (
              <Link to="/dashboard" className="warm-btn inline-flex items-center gap-1.5 px-4 py-2 text-sm">
                Go to app <ArrowRight className="size-3.5" />
              </Link>
            ) : (
              <>
                <Link to="/auth" className="hidden sm:inline-flex items-center px-3 py-1.5 text-sm rounded-md transition-colors" style={{ color: 'var(--warm-muted)' }}>
                  Log in
                </Link>
                <Link to="/auth" tabIndex={mobileOpen ? -1 : undefined} className={`warm-btn inline-flex items-center px-4 py-2 text-sm transition-opacity duration-300 ${mobileOpen ? 'opacity-0 pointer-events-none lg:opacity-100 lg:pointer-events-auto' : ''}`}>
                  Start free
                </Link>
              </>
            )}
            <button
              onClick={() => setMobileOpen((o) => !o)}
              className="mm-burger lg:hidden relative h-9 w-9 rounded-lg flex items-center"
              style={{ color: 'var(--warm-text)' }}
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileOpen}
              aria-haspopup="dialog"
            >
              <span /><span />
            </button>
          </div>
        </div>
      </header>

      <MobileMenuDrawer open={mobileOpen} onClose={closeMenu} signedIn={!!user} />
    </>
  );
}
